import { useCallback, useEffect } from 'react';

import { alignToolForGeneration } from 'src/data/generationTool';
import { isBareboneProjectId } from 'src/data/project';
import { DEFAULT_SCORING_METHOD } from 'src/data/scoring';
import { queryProjectScoringMethod } from 'src/graphql/scoring';
import { captureMessage } from 'src/lib/sentry';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useNotificationActions } from 'src/store';

import { MainStore, useDocumentViewActions } from '../store';

const useMainState = createStateSelectorHook(MainStore, ['project', 'generationTool']);

export const useMaintainCurrentScoringMethod = () => {
  const notifications = useNotificationActions();
  const documentViewActions = useDocumentViewActions();

  const { project, generationTool } = useMainState();
  const projectId = project ? project.id : null;

  const reportError = useCallback(
    (error, params) => {
      notifications.displayWarning(
        `Couldn't load last used scoring method for ${params.generationTool}`
      );

      captureMessage('Error while load last used scoring method', {
        level: 'warning',
        extra: {
          error: error.toString(),
          ...params,
        },
      });
    },
    [notifications]
  );

  useEffect(() => {
    if (!projectId || !generationTool || isBareboneProjectId(projectId)) {
      return;
    }

    const fetchScoringMethod = async () => {
      documentViewActions.setLoadingScoringMethod(true);
      try {
        const scoringMethod = await queryProjectScoringMethod({
          projectId,
          generationTool: alignToolForGeneration(generationTool),
        });

        documentViewActions.setScoringMethod(scoringMethod || DEFAULT_SCORING_METHOD);
      } catch (error) {
        reportError(error, { projectId, generationTool });
      } finally {
        documentViewActions.setLoadingScoringMethod(false);
      }
    };

    fetchScoringMethod();
  }, [projectId, generationTool, documentViewActions, reportError]);
};
